/* A QR code, computed here and drawn as SVG. Byte mode, versions 1–10,
   error correction level L or M.

   Ported from the static portal, where it was written because that portal could
   not fetch and could not carry an image file. Neither reason survives — and it
   is still the right thing: an image would be one more asset to keep in step
   with a domain that has already changed once, and a code fetched from a QR
   service tells that service which room is looking at what. Ten versions are
   plenty, because the only thing ever encoded is the address of this material.

   The algorithm is unchanged from the version that has been scanned by rooms
   full of teachers; the port was checked against it byte for byte. */

/* ---------- GF(256), primitive polynomial 0x11d ---------- */

const EXP = new Array<number>(512);
const LOG = new Array<number>(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d; }
  for (let j = 255; j < 512; j++) EXP[j] = EXP[j - 255]!;
}

const mul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!);

/* Xor into place, said once. `a[i] ^= v` is a compound assignment the checker
   cannot prove safe under noUncheckedIndexedAccess, and `(a[i] ?? 0)` on every
   line of the arithmetic below reads worse than one helper does. */
function xor(a: number[], i: number, v: number): void {
  a[i] = (a[i] ?? 0) ^ v;
}

function polyMul(a: number[], b: number[]): number[] {
  const r = new Array<number>(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) xor(r, i + j, mul(a[i]!, b[j]!));
  }
  return r;
}

function genPoly(n: number): number[] {
  let g = [1];
  for (let i = 0; i < n; i++) g = polyMul(g, [1, EXP[i]!]);
  return g;
}

function ecBlock(data: number[], n: number): number[] {
  const g = genPoly(n);
  const res = data.concat(new Array<number>(n).fill(0));
  for (let i = 0; i < data.length; i++) {
    const c = res[i]!;
    if (!c) continue;
    for (let j = 1; j < g.length; j++) xor(res, i + j, mul(g[j]!, c));
  }
  return res.slice(data.length);
}

/* ---------- version tables (1–10) ---------- */

export type Level = "L" | "M";

/* [ec codewords per block, group1 blocks, group1 data, group2 blocks, group2 data] */
const BLOCKS: Record<Level, number[][]> = {
  L: [[7, 1, 19, 0, 0], [10, 1, 34, 0, 0], [15, 1, 55, 0, 0], [20, 1, 80, 0, 0],
      [26, 1, 108, 0, 0], [18, 2, 68, 0, 0], [20, 2, 78, 0, 0], [24, 2, 97, 0, 0],
      [30, 2, 116, 0, 0], [18, 2, 68, 2, 69]],
  M: [[10, 1, 16, 0, 0], [16, 1, 28, 0, 0], [26, 1, 44, 0, 0], [18, 2, 32, 0, 0],
      [24, 2, 43, 0, 0], [16, 4, 27, 0, 0], [18, 4, 31, 0, 0], [22, 2, 38, 2, 39],
      [22, 3, 36, 2, 37], [26, 4, 43, 1, 44]],
};

const ALIGN: number[][] = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34],
                           [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];

const EC_BITS: Record<Level, number> = { L: 1, M: 0 };

function dataCapacity(ver: number, level: Level): number {
  const b = BLOCKS[level][ver - 1]!;
  return b[1]! * b[2]! + b[3]! * b[4]!;
}

/* ---------- encoding ---------- */

function bitBuffer() {
  const bytes: number[] = [];
  let len = 0;
  return {
    put(value: number, bits: number) {
      for (let i = bits - 1; i >= 0; i--) {
        if ((len & 7) === 0) bytes.push(0);
        if ((value >>> i) & 1) bytes[len >> 3]! |= 0x80 >> (len & 7);
        len++;
      }
    },
    get length() { return len; },
    bytes,
  };
}

function encode(text: string, level: Level): { version: number; codewords: number[] } {
  const data = Array.from(new TextEncoder().encode(text));

  let ver = 1;
  for (; ver <= 10; ver++) {
    const lenBits = ver < 10 ? 8 : 16;
    if (4 + lenBits + data.length * 8 <= dataCapacity(ver, level) * 8) break;
  }
  if (ver > 10) throw new Error("QR: text too long");

  const cap = dataCapacity(ver, level);
  const bb = bitBuffer();
  bb.put(4, 4);                                   /* byte mode */
  bb.put(data.length, ver < 10 ? 8 : 16);
  for (const byte of data) bb.put(byte, 8);
  bb.put(0, Math.min(4, cap * 8 - bb.length));    /* terminator */
  while (bb.length % 8 !== 0) bb.put(0, 1);

  const out = bb.bytes;
  const pad = [0xec, 0x11];
  for (let p = 0; out.length < cap; p++) out.push(pad[p % 2]!);

  /* split into blocks, compute error correction, interleave */
  const spec = BLOCKS[level][ver - 1]!;
  const ecLen = spec[0]!;
  const blocks: number[][] = [];
  const ecs: number[][] = [];
  let at = 0;
  for (let b = 0; b < spec[1]! + spec[3]!; b++) {
    const n = b < spec[1]! ? spec[2]! : spec[4]!;
    const blk = out.slice(at, at + n);
    at += n;
    blocks.push(blk);
    ecs.push(ecBlock(blk, ecLen));
  }

  const codewords: number[] = [];
  const maxData = Math.max(spec[2]!, spec[4]!);
  for (let k = 0; k < maxData; k++) {
    for (const blk of blocks) if (k < blk.length) codewords.push(blk[k]!);
  }
  for (let k = 0; k < ecLen; k++) for (const ec of ecs) codewords.push(ec[k]!);

  return { version: ver, codewords };
}

/* ---------- matrix ---------- */

function formatBits(level: Level, mask: number): number {
  const d = (EC_BITS[level] << 3) | mask;
  let v = d << 10;
  for (let i = 14; i >= 10; i--) if ((v >> i) & 1) v ^= 0x537 << (i - 10);
  return ((d << 10) | v) ^ 0x5412;
}

function versionBits(ver: number): number {
  let v = ver << 12;
  for (let i = 17; i >= 12; i--) if ((v >> i) & 1) v ^= 0x1f25 << (i - 12);
  return (ver << 12) | v;
}

function maskAt(mask: number, r: number, c: number): boolean {
  switch (mask) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return (r * c) % 2 + (r * c) % 3 === 0;
    case 6: return ((r * c) % 2 + (r * c) % 3) % 2 === 0;
    default: return ((r + c) % 2 + (r * c) % 3) % 2 === 0;
  }
}

function buildMatrix(ver: number, level: Level, codewords: number[], mask: number): number[][] {
  const size = 17 + 4 * ver;
  const m: number[][] = [];
  const fn: boolean[][] = [];
  for (let r = 0; r < size; r++) {
    m.push(new Array<number>(size).fill(0));
    fn.push(new Array<boolean>(size).fill(false));
  }
  const set = (x: number, y: number, dark: boolean | number) => {
    m[y]![x] = dark ? 1 : 0;
    fn[y]![x] = true;
  };

  /* finder patterns with their separators */
  const finder = (x0: number, y0: number) => {
    for (let dy = -1; dy <= 7; dy++) {
      for (let dx = -1; dx <= 7; dx++) {
        const x = x0 + dx, y = y0 + dy;
        if (x < 0 || x >= size || y < 0 || y >= size) continue;
        const d = Math.max(Math.abs(dx - 3), Math.abs(dy - 3));
        set(x, y, d !== 2 && d !== 4);
      }
    }
  };
  finder(0, 0); finder(size - 7, 0); finder(0, size - 7);

  /* timing patterns */
  for (let i = 0; i < size; i++) {
    if (!fn[6]![i]) set(i, 6, i % 2 === 0);
    if (!fn[i]![6]) set(6, i, i % 2 === 0);
  }

  /* alignment patterns, minus the three that would sit on a finder */
  const pos = ALIGN[ver - 1]!;
  for (let i = 0; i < pos.length; i++) {
    for (let j = 0; j < pos.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === pos.length - 1) ||
          (i === pos.length - 1 && j === 0)) continue;
      for (let ay = -2; ay <= 2; ay++) {
        for (let ax = -2; ax <= 2; ax++) {
          set(pos[j]! + ax, pos[i]! + ay, Math.max(Math.abs(ax), Math.abs(ay)) !== 1);
        }
      }
    }
  }

  /* format information, both copies, plus the always-dark module */
  const fb = formatBits(level, mask);
  const bit = (v: number, i: number) => (v >>> i) & 1;
  for (let i = 0; i <= 5; i++) set(8, i, bit(fb, i));
  set(8, 7, bit(fb, 6)); set(8, 8, bit(fb, 7)); set(7, 8, bit(fb, 8));
  for (let i = 9; i < 15; i++) set(14 - i, 8, bit(fb, i));
  for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(fb, i));
  for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(fb, i));
  set(8, size - 8, 1);

  /* version information (7 and up) */
  if (ver >= 7) {
    const vb = versionBits(ver);
    for (let i = 0; i < 18; i++) {
      const b = bit(vb, i), a = size - 11 + (i % 3), row = Math.floor(i / 3);
      set(a, row, b); set(row, a, b);
    }
  }

  /* data, zigzag from the bottom right, skipping the timing column */
  let bitIndex = 0;
  const total = codewords.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (fn[y]![x] || bitIndex >= total) continue;
        m[y]![x] = (codewords[bitIndex >>> 3]! >>> (7 - (bitIndex & 7))) & 1;
        bitIndex++;
      }
    }
  }

  /* mask every module that is not a function pattern */
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (fn[r]![c]) continue;
      if (maskAt(mask, r, c)) xor(m[r]!, c, 1);
    }
  }
  return m;
}

/* the four penalty rules of the standard; the lowest score wins the mask */
function penalty(m: number[][]): number {
  const size = m.length;
  let score = 0, dark = 0;
  const FIND = "10111010000";
  const REV = FIND.split("").reverse().join("");

  const runs = (line: number[]) => {
    let s = 0, run = 1;
    for (let k = 1; k < line.length; k++) {
      if (line[k] === line[k - 1]) run++;
      else { if (run >= 5) s += 3 + (run - 5); run = 1; }
    }
    if (run >= 5) s += 3 + (run - 5);
    const padded = "0000" + line.join("") + "0000";
    for (const needle of [FIND, REV]) {
      let at = padded.indexOf(needle);
      while (at !== -1) { s += 40; at = padded.indexOf(needle, at + 1); }
    }
    return s;
  };

  for (let r = 0; r < size; r++) score += runs(m[r]!);
  for (let c = 0; c < size; c++) {
    const col: number[] = [];
    for (let r = 0; r < size; r++) col.push(m[r]![c]!);
    score += runs(col);
  }
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = m[r]![c];
      if (v === m[r]![c + 1] && v === m[r + 1]![c] && v === m[r + 1]![c + 1]) score += 3;
    }
  }
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (m[r]![c]) dark++;
  score += Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5) * 10;
  return score;
}

export function matrix(text: string, level: Level = "M"): number[][] {
  const enc = encode(text, level);
  let best: number[][] = [];
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const m = buildMatrix(enc.version, level, enc.codewords, mask);
    const s = penalty(m);
    if (s < bestScore) { bestScore = s; best = m; }
  }
  return best;
}

export interface QrOptions {
  level?: Level;
  /* The quiet zone. Four modules is what the standard asks for and what a phone
     camera needs to find the code at all. */
  quiet?: number;
  dark?: string;
  light?: string;
  label?: string;
}

/* One path of horizontal runs rather than a rect per module: a version 4 code is
   a thousand modules, and a thousand elements is a page that stutters when it
   opens in front of a room. */
export function qrSvg(text: string, opts: QrOptions = {}): string {
  const m = matrix(text, opts.level ?? "M");
  const quiet = opts.quiet ?? 4;
  const n = m.length;
  const side = n + quiet * 2;
  const dark = opts.dark ?? "#16150f";
  const light = opts.light ?? "#ffffff";

  const d: string[] = [];
  for (let r = 0; r < n; r++) {
    let c = 0;
    while (c < n) {
      if (!m[r]![c]) { c++; continue; }
      const start = c;
      while (c < n && m[r]![c]) c++;
      d.push(`M${start + quiet} ${r + quiet}h${c - start}v1h${-(c - start)}z`);
    }
  }

  const label = (opts.label ?? "QR").replace(/[<>&"]/g, "");
  return `<svg class="qrsvg" viewBox="0 0 ${side} ${side}" `
    + `xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges" `
    + `role="img" aria-label="${label}">`
    + `<rect width="${side}" height="${side}" fill="${light}"/>`
    + `<path fill="${dark}" d="${d.join("")}"/>`
    + `</svg>`;
}
